import React, { useEffect, useState, useRef } from "react";
import { styled } from "@mui/material/styles";
import { MapContainer, TileLayer } from "react-leaflet";
import Geoloc from "./Geoloc";
import { LatLng } from "leaflet/dist/leaflet-src.esm";
import {
  CircularProgress,
  Container,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  FormControlLabel,
  Checkbox,
  Button,
  Dialog,
} from "@mui/material";
import { Paper, TextField } from "@mui/material";
import {
  Cancel,
  Delete,
  Edit,
  LocationOn,
  MyLocation,
  Save,
} from "@mui/icons-material";
import { ActionsPicker, guidGenerator, useViewport } from "../../helpers";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AddIcon from "@mui/icons-material/Add";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import i18next from "i18next";
import dayjs from "dayjs";
import MarkerForm from "./MarkerForm";
import AddMarkerIcon from "../../assets/add-location.png";

const PREFIX = "MyMapContainer";

const classes = {
  createGeneralContainer: `${PREFIX}-createGeneralContainer`,
  generalContainer: `${PREFIX}-generalContainer`,
  columnDisplay: `${PREFIX}-columnDisplay`,
  visibleContainer: `${PREFIX}-visibleContainer`,
  createMapContainerManager: `${PREFIX}-createMapContainerManager`,
  fullWidthMap: `${PREFIX}-fullWidthMap`,
  mapContainerManager: `${PREFIX}-mapContainerManager`,
  createMarkersContainer: `${PREFIX}-createMarkersContainer`,
  markersContainer: `${PREFIX}-markersContainer`,
  listContainer: `${PREFIX}-listContainer`,
  arrowsContainer: `${PREFIX}-arrowsContainer`,
  addMyPositionButton: `${PREFIX}-addMyPositionButton`,
  linkedExparienceTitle: `${PREFIX}-linkedExparienceTitle`,
  datePickersContainer: `${PREFIX}-datePickersContainer`,
  addMarkerIconStyle: `${PREFIX}-addMarkerIconStyle`,
};

const Root = styled("div")(({ theme }) => ({
  // doubler les classname pour le create Form ne soit jamais droite/gauche
  [`& .${classes.createGeneralContainer}`]: {
    display: "flex",
    flexDirection: "column",
  },

  [`& .${classes.generalContainer}`]: {
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("lg")]: {
      minWidth: "450px",
      flexDirection: "row",
    },
  },

  [`& .${classes.columnDisplay}`]: {
    display: "flex",
    flexDirection: "column",
  },

  [`& .${classes.visibleContainer}`]: {
    textAlign: "center",
    marginTop: "-0.5rem",
    marginBottom: "0.5rem",
  },

  [`& .${classes.createMapContainerManager}`]: {
    width: "100%",
  },

  [`& .${classes.fullWidthMap}`]: {
    marginTop: "0.5rem",
    width: "100%",
    "& > div": {
      height: "500px !important",
    },
    "& > div > div": {
      height: "500px !important",
    },
    "& > div > div > div": {
      height: "500px !important",
    },
  },

  [`& .${classes.mapContainerManager}`]: {
    width: "100%",
    [theme.breakpoints.up("lg")]: {
      width: "48%",
    },
  },

  [`& .${classes.createMarkersContainer}`]: {
    width: "100%",
  },

  [`& .${classes.markersContainer}`]: {
    width: "100%",
    [theme.breakpoints.up("lg")]: {
      width: "48%",
      maxHeight: "600px",
    },
  },

  [`& .${classes.listContainer}`]: {
    [theme.breakpoints.up("lg")]: {
      maxHeight: "505px",
      overflow: "auto",
      "&::-webkit-scrollbar": {
        width: "5px",
        height: "5px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "rgb(181 154 79 / 58%)",
        borderRadius: "10px",
      },
    },
  },

  [`& .${classes.arrowsContainer}`]: {
    display: "flex",
    flexDirection: "column",
    marginRight: "1rem",
  },

  [`& .${classes.addMyPositionButton}`]: {
    transform: "translateY(-5px)",
  },

  [`& .${classes.linkedExparienceTitle}`]: {
    color: theme.palette.primary.main,
    fontSize: "17px",
  },

  [`& .${classes.datePickersContainer}`]: {
    display: "flex",
    justifyContent: "space-around",
    "& > div": {
      width: "40%",
    },
    marginBottom: "1rem",
    paddingLeft: "1.5rem",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      justifyContent: "space-between",
      height: "125px",
      padding: "0 0.5rem",
      "& > div": {
        width: "100%",
      },
    },
  },

  [`& .${classes.addMarkerIconStyle}`]: {
    width: "28px",
    height: "28px",
  },
}));

export default function MyMapContainer(props) {
  const { t } = useTranslation();

  const { width } = useViewport();
  const [error, setError] = useState(null);
  const [myPosition, setMyPosition] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [markerToFlyTo, setMarkerToFlyTo] = useState(null);
  const [editable, setEditable] = useState(-1);
  const [oldTitle, setOldTitle] = useState();
  const [initalized, setInitialized] = useState(false);
  const [markerFromBackdrop, setMarkerFromBackdrop] = useState(false);
  const filterLength = useRef();

  useEffect(() => {
    if ("geolocation" in navigator && !props.dashboardMap) {
      navigator.geolocation.getCurrentPosition(
        function (position) {
          setMyPosition(
            new LatLng(position.coords.latitude, position.coords.longitude)
          );
        },
        function (error) {
          setError(error.message);
        }
      );
    }

    if (props.positions) {
      setMarkers(() =>
        props.positions.map((p) => {
          return {
            position: !props.dashboardMap
              ? new LatLng(p.lat, p.long)
              : new LatLng(p.lat, p.lng),
            title: p.title,
            radius: p.radius || (!props.dashboardMap ? 1000 : 1),
            visible: p.visible,
            free: p.free,
            controlDate: p.controlDate,
            startDate: p.startDate || null,
            endDate: p.endDate || null,
          };
        })
      );
    }
  }, []);

  React.useEffect(() => {
    if (props.positions?.length > 0) {
      let tmp = [];
      props.positions.forEach((p) => {
        const tmpMarker = {
          position: !props.dashboardMap
            ? new LatLng(p.lat, p.long)
            : new LatLng(p.lat, p.lng),
          title: p.title,
          radius: p.radius || (!props.dashboardMap ? 1000 : 1),
          visible: p.visible,
          free: p.free,
          controlDate: p.controlDate,
          startDate: p.startDate || null,
          endDate: p.endDate || null,
        };
        if (props.visualize) {
          tmpMarker.displayName =
            p.exparience?.displayName ||
            p.exparience?.name ||
            p.displayName ||
            p.name;
          tmpMarker.imageUrl = p.exparience?.logoUrl || null;
        }
        tmp.push(tmpMarker);
      });
      setMarkers(tmp);
      if (
        initalized &&
        props.dashboardMap &&
        filterLength.current === props.filterValues.length
      ) {
        setMarkerToFlyTo(tmp[tmp.length - 1].position);
        filterLength.current = props.filterValues.length;
      } else {
        setInitialized(true);
        if (props.dashboardMap)
          filterLength.current = props.filterValues.length;
      }
    }
  }, [props.positions]);

  function addMarker(marker) {
    const newMarkers = [marker, ...markers];
    setMarkers(newMarkers);
    save(newMarkers);
  }

  function removeMarker(index) {
    const newMarkers = [...markers];
    newMarkers.splice(index, 1);
    setMarkers(newMarkers);
    save(newMarkers);
  }

  function flyTo(position) {
    setMarkerToFlyTo(position);
  }

  function handleChangeMarkerTitle(idx, value) {
    const newMarkers = [...markers];
    newMarkers[idx].title = value;
    setMarkers(newMarkers);
    save();
  }

  function handleChangeVisible(idx, value) {
    const newMarkers = [...markers];
    newMarkers[idx].visible = value;
    setMarkers(newMarkers);
    save();
  }

  function handleChangeFree(idx, value) {
    const newMarkers = [...markers];
    newMarkers[idx].free = value;
    setMarkers(newMarkers);
    save();
  }

  function handleChangeControlDate(idx, value) {
    const newMarkers = [...markers];
    newMarkers[idx].controlDate = value;
    setMarkers(newMarkers);
    save();
  }

  function handleChangeDates(idx, key, value) {
    const newMarkers = [...markers];
    newMarkers[idx] = { ...newMarkers[idx], [key]: value };
    setMarkers(newMarkers);
    save(newMarkers);
  }

  function handleChangeMarkerRadius(idx, value) {
    const newMarkers = [...markers];
    newMarkers[idx].radius = value;
    setMarkers(newMarkers);
    save();
  }

  function save(newMarkers) {
    const zeMarkers = newMarkers ? newMarkers : markers;
    props.save(
      zeMarkers.map((m) => {
        return {
          lat: m.position.lat,
          long: m.position.lng,
          type: "Point",
          title: m.title,
          guid: guidGenerator(),
          radius: m.radius,
          visible: m.visible,
          free: m.free,
          controlDate: m.controlDate,
          startDate: m.startDate || null,
          endDate: m.endDate || null,
        };
      })
    );
  }

  const handleMarkerPosition = (index, order) => {
    if (
      (order === "up" && index === 0) ||
      (order === "down" && index === markers.length - 1)
    )
      return;
    let tmpMarkers = [...markers];
    if (order === "up") {
      const tmp = tmpMarkers[index];
      tmpMarkers[index] = tmpMarkers[index - 1];
      tmpMarkers[index - 1] = tmp;
    } else {
      const tmp = tmpMarkers[index];
      tmpMarkers[index] = tmpMarkers[index + 1];
      tmpMarkers[index + 1] = tmp;
    }
    setMarkers(tmpMarkers);
    save(tmpMarkers);
  };

  useEffect(() => {
    if (props.backdropInfos?.position?.coordinates && props.dashboardMap) {
      setMarkerToFlyTo(
        new LatLng(
          props.backdropInfos.position.coordinates[1],
          props.backdropInfos.position.coordinates[0]
        )
      );
    }
  }, [props.backdropInfos]);

  useEffect(() => {
    if (markers.length > 0) {
      let isDisable = false;
      markers.forEach((m) => {
        if (
          m.controlDate &&
          ((m.startDate && !m.endDate) || (!m.startDate && m.endDate))
        )
          isDisable = true;
      });
      props.setDatesDisable(isDisable);
    }
  }, [markers]);

  const closeMarkerBackdrop = () => {
    setMarkerFromBackdrop(false);
  };

  return props.dashboardMap || (!props.dashboardMap && myPosition) || error ? (
    <Root>
      {!props.visualize && (
        <Dialog
          open={markerFromBackdrop}
          PaperProps={{ style: { margin: "inherit !important" } }}
          onClose={(e, reason) => {
            e.preventDefault();
            if (reason && reason === "backdropClick") return;
            closeMarkerBackdrop();
          }}
        >
          <MarkerForm
            onClose={closeMarkerBackdrop}
            saveMarker={addMarker}
            flyTo={flyTo}
          />
        </Dialog>
      )}
      <div
        className={
          !props.isCreateForm
            ? classes.generalContainer
            : classes.createGeneralContainer
        }
      >
        <div className="geoloc">
          <div
            className={
              props.dashboardMap
                ? classes.fullWidthMap
                : !props.isCreateForm
                ? classes.mapContainerManager
                : classes.createMapContainerManager
            }
          >
            <div className="details-on-map show-map">
              <div className="paper-map">
                <div className="map-side"></div>
                <div className="map-side"></div>
                <div className="map-side"></div>
                <div className="map-side"></div>
              </div>
              <MapContainer
                className="my-map"
                center={
                  error || props.dashboardMap
                    ? [47.2180499461086, -1.5576933846545309]
                    : myPosition
                }
                zoom={props.dashboardMap ? 2 : 13}
                scrollWheelZoom={false}
              >
                <TileLayer
                  attribution='&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="http://cartodb.com/attributions">CartoDB</a>'
                  url="https://cartodb-basemaps-{s}.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png"
                />
                <Geoloc
                  myPosition={myPosition}
                  setMyPosition={setMyPosition}
                  errorMyPosition={error}
                  addMarker={addMarker}
                  removeMarker={removeMarker}
                  markers={markers}
                  markerToFlyTo={markerToFlyTo}
                  setMarkerToFlyTo={setMarkerToFlyTo}
                  visualize={props.visualize}
                  dashboardMap={props.dashboardMap}
                />
              </MapContainer>
            </div>
          </div>
          {!props.dashboardMap && (
            <Container
              disableGutters
              className={
                !props.isCreateForm
                  ? classes.markersContainer
                  : classes.createMarkersContainer
              }
              style={{ marginBottom: !props.visualize ? "4rem" : "inherit" }}
            >
              <Paper
                elevation={20}
                className="padding-top-1"
                style={{ height: props.visualize ? "inherit" : "100%" }}
              >
                <div
                  className="padding-lr-1"
                  style={{
                    borderBottom: width > 600 ? "1px dashed #D3AF5F" : "none",
                  }}
                >
                  <h1 className="inline-block">{t("positions.title")}</h1>
                  <IconButton
                    aria-label="locate"
                    onClick={() => flyTo(myPosition)}
                    color="primary"
                    sx={{
                      transform: width > 600 ? "translateY(-4px)" : "none",
                    }}
                    size="large"
                  >
                    <MyLocation />
                  </IconButton>
                  {!props.visualize && (
                    <>
                      <Button
                        className={classes.addMyPositionButton}
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const marker = {
                            position: myPosition,
                            title: t("positions.myPosition"),
                            radius: 1000,
                            visible: false,
                            free: false,
                          };
                          addMarker(marker);
                        }}
                      >
                        {t("positions.addMyPosition")}
                      </Button>
                      <IconButton
                        color="primary"
                        onClick={() => setMarkerFromBackdrop(true)}
                        size="large"
                        sx={{
                          transform: "translateY(-6px)",
                        }}
                      >
                        <img
                          src={AddMarkerIcon}
                          alt="add-marker-icon"
                          className={classes.addMarkerIconStyle}
                        />
                      </IconButton>
                    </>
                  )}
                </div>
                {(!markers || markers.length === 0) && (
                  <div className="padding-lr-1">
                    <h4>{t("positions.no")}</h4>
                  </div>
                )}
                <List
                  dense
                  className={!props.isCreateForm ? classes.listContainer : ""}
                  style={{
                    maxHeight:
                      props.visualize && width > 600 ? "460px" : "505px",
                    overflow: "auto",
                  }}
                >
                  {markers.map((m, idx) => {
                    return (
                      <div key={`div-item-${idx}`}>
                        {editable === idx ? (
                          <ListItem
                            key={`item-${idx}`}
                            className={clsx("flex75")}
                          >
                            <ListItemText
                              primary={
                                <div>
                                  <div>
                                    <TextField
                                      variant="outlined"
                                      margin="normal"
                                      fullWidth
                                      label={t("positions.input")}
                                      id={`textfield-${idx}`}
                                      value={m.title}
                                      onChange={(e) =>
                                        handleChangeMarkerTitle(
                                          idx,
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                  <div>
                                    <TextField
                                      variant="outlined"
                                      margin="normal"
                                      fullWidth
                                      label={t("positions.inputRadius")}
                                      value={m.radius || 100}
                                      id={`textfield-radius-${idx}`}
                                      name={`textfield-radius-${idx}`}
                                      type="number"
                                      onChange={(e) =>
                                        handleChangeMarkerRadius(
                                          idx,
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                </div>
                              }
                              className={clsx("flex75")}
                            />
                            <ListItemSecondaryAction
                              className={classes.columnDisplay}
                            >
                              <IconButton
                                edge="end"
                                aria-label="edit"
                                onClick={() => setEditable(-1)}
                                size="large"
                              >
                                <Save />
                              </IconButton>
                              <IconButton
                                edge="end"
                                aria-label="edit"
                                onClick={() => {
                                  setEditable(-1);
                                  handleChangeMarkerTitle(idx, oldTitle);
                                }}
                                size="large"
                              >
                                <Cancel />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ) : (
                          <ListItem
                            key={`item-${idx}`}
                            className={clsx("flex75")}
                          >
                            {!props.visualize && (
                              <div className={classes.arrowsContainer}>
                                <IconButton
                                  edge="end"
                                  aria-label="edit"
                                  onClick={() =>
                                    handleMarkerPosition(idx, "up")
                                  }
                                  size="large"
                                >
                                  <KeyboardArrowUpIcon />
                                </IconButton>
                                <IconButton
                                  edge="end"
                                  aria-label="locate"
                                  onClick={() =>
                                    handleMarkerPosition(idx, "down")
                                  }
                                  size="large"
                                >
                                  <KeyboardArrowDownIcon />
                                </IconButton>
                              </div>
                            )}
                            <ListItemText
                              primary={
                                <div>
                                  {props.visualize && (
                                    <div>
                                      <span>{t("journeys.exparience")}</span>
                                      <span
                                        className={
                                          classes.linkedExparienceTitle
                                        }
                                      >
                                        {m.displayName}
                                      </span>
                                    </div>
                                  )}
                                  <div>
                                    <TextField
                                      variant="outlined"
                                      margin="normal"
                                      fullWidth
                                      label={t("positions.input")}
                                      value={m.title}
                                      id={`textfield-${idx}`}
                                      name={`textfield-${idx}`}
                                      disabled
                                    />
                                  </div>
                                  <div>
                                    <TextField
                                      variant="outlined"
                                      margin="normal"
                                      fullWidth
                                      label={t("positions.inputRadius")}
                                      value={m.radius || 100}
                                      id={`textfield-radius-${idx}`}
                                      name={`textfield-radius-${idx}`}
                                      disabled
                                    />
                                  </div>
                                </div>
                              }
                              className={clsx("flex75")}
                            />
                            <ListItemSecondaryAction
                              className={classes.columnDisplay}
                            >
                              {!props.visualize && (
                                <IconButton
                                  edge="end"
                                  aria-label="edit"
                                  onClick={() => {
                                    setEditable(idx);
                                    setOldTitle(m.title);
                                  }}
                                  size="large"
                                >
                                  <Edit />
                                </IconButton>
                              )}
                              <IconButton
                                edge="end"
                                aria-label="locate"
                                onClick={() => flyTo(m.position)}
                                size="large"
                              >
                                <LocationOn />
                              </IconButton>
                              {!props.visualize && (
                                <IconButton
                                  edge="end"
                                  aria-label="delete"
                                  onClick={() => removeMarker(idx)}
                                  size="large"
                                >
                                  <Delete />
                                </IconButton>
                              )}
                            </ListItemSecondaryAction>
                          </ListItem>
                        )}
                        {!props.visualize && (
                          <>
                            <div className={classes.visibleContainer}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    color={"primary"}
                                    onChange={() =>
                                      handleChangeVisible(idx, !m.visible)
                                    }
                                    checked={m.visible || false}
                                  />
                                }
                                label={t("myExpariences.form.visible")}
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    color={"primary"}
                                    onChange={() =>
                                      handleChangeFree(idx, !m.free)
                                    }
                                    checked={m.free || false}
                                  />
                                }
                                label={t("myExpariences.form.free")}
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    color={"primary"}
                                    onChange={() =>
                                      handleChangeControlDate(
                                        idx,
                                        !m.controlDate
                                      )
                                    }
                                    checked={m.controlDate || false}
                                  />
                                }
                                label={t("myExpariences.form.accessByDates")}
                              />
                            </div>
                            {m.controlDate && (
                              <div className={classes.datePickersContainer}>
                                <LocalizationProvider
                                  dateAdapter={AdapterDayjs}
                                  adapterLocale={i18next.language.substring(
                                    0,
                                    2
                                  )}
                                >
                                  <DatePicker
                                    disablePast
                                    label={t("myExpariences.form.startDate")}
                                    slots={{
                                      actionBar: ActionsPicker,
                                    }}
                                    slotProps={{
                                      actionBar: {
                                        actions: ["clear", "cancel", "accept"],
                                      },
                                    }}
                                    className={classes.marginBottomStyle}
                                    onChange={(e) =>
                                      handleChangeDates(
                                        idx,
                                        "startDate",
                                        e ? dayjs(e).format("YYYY-MM-DD") : null
                                      )
                                    }
                                    onError={(err) =>
                                      err
                                        ? props.setDatesDisable(true)
                                        : props.setDatesDisable(false)
                                    }
                                    value={
                                      m.startDate ? dayjs(m.startDate) : null
                                    }
                                    sx={{
                                      marginRight: "1rem",
                                    }}
                                  />
                                  <DatePicker
                                    disablePast
                                    minDate={
                                      m.startDate
                                        ? dayjs(m.startDate).add(1, "day")
                                        : null
                                    }
                                    label={t("myExpariences.form.endDate")}
                                    slots={{
                                      actionBar: ActionsPicker,
                                    }}
                                    slotProps={{
                                      actionBar: {
                                        actions: ["clear", "cancel", "accept"],
                                      },
                                    }}
                                    className={classes.marginBottomStyle}
                                    onChange={(e) =>
                                      handleChangeDates(
                                        idx,
                                        "endDate",
                                        e ? dayjs(e).format("YYYY-MM-DD") : null
                                      )
                                    }
                                    onError={(err) =>
                                      err
                                        ? props.setDatesDisable(true)
                                        : props.setDatesDisable(false)
                                    }
                                    value={m.endDate ? dayjs(m.endDate) : null}
                                  />
                                </LocalizationProvider>
                              </div>
                            )}
                          </>
                        )}
                        {idx < markers.length - 1 && <Divider />}
                      </div>
                    );
                  })}
                </List>
              </Paper>
            </Container>
          )}
        </div>
      </div>
    </Root>
  ) : (
    <div className="loader">
      <CircularProgress />
    </div>
  );
}
